import React, {
  ForwardedRef,
  forwardRef,
  JSXElementConstructor,
  ReactNode,
} from 'react';
import { DOMProps, DOMAttributes } from '@react-types/shared';

import { FlexStyleProps, flexStylePropsHandler } from '../../Flex/src/Flex';
import { GridStyleProps, gridStylePropsHandler } from '../../Grid/src/Grid';
import {
  commonStyleHandlers,
  ResponsiveCssProp,
  SapphireCommonStyleProps,
  SapphireStyleProps,
  StyleHandlers,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

export interface ViewProps
  extends GlobalDomAttributes,
    SapphireStyleProps,
    SapphireViewStyleProps,
    DOMProps,
    Omit<
      DOMAttributes,
      'children' | 'style' | 'className' | 'dangerouslySetInnerHTML'
    > {
  /**
   * The element to render as the node.
   * @default: 'div'
   */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  elementType?: string | JSXElementConstructor<any>;
  /**
   * Children to be displayed in the View.
   */
  children?: ReactNode;
}

export interface SapphireViewStyleProps
  extends SapphireCommonStyleProps,
    FlexStyleProps,
    GridStyleProps {
  display?: ResponsiveCssProp<'display'>;

  backgroundColor?: ResponsiveCssProp<'backgroundColor', 'color'>;
  color?: ResponsiveCssProp<'color', 'color'>;

  border?: ResponsiveCssProp<'border'>;
  borderWidth?: ResponsiveCssProp<'borderWidth', 'size'>;
  borderColor?: ResponsiveCssProp<'borderColor', 'color'>;
  borderStyle?: ResponsiveCssProp<'borderColor'>;
  borderLeft?: ResponsiveCssProp<'borderLeft'>;
  borderLeftColor?: ResponsiveCssProp<'borderLeftColor', 'color'>;
  borderLeftWidth?: ResponsiveCssProp<'borderLeftWidth', 'size'>;
  borderLeftStyle?: ResponsiveCssProp<'borderLeftStyle'>;
  borderRight?: ResponsiveCssProp<'borderRight'>;
  borderRightColor?: ResponsiveCssProp<'borderRightColor', 'color'>;
  borderRightWidth?: ResponsiveCssProp<'borderRightWidth', 'size'>;
  borderRightStyle?: ResponsiveCssProp<'borderRightStyle'>;
  borderTop?: ResponsiveCssProp<'borderTop'>;
  borderTopColor?: ResponsiveCssProp<'borderTopColor', 'color'>;
  borderTopWidth?: ResponsiveCssProp<'borderTopWidth', 'size'>;
  borderTopStyle?: ResponsiveCssProp<'borderTopStyle'>;
  borderBottom?: ResponsiveCssProp<'borderBottom'>;
  borderBottomColor?: ResponsiveCssProp<'borderBottomColor', 'color'>;
  borderBottomWidth?: ResponsiveCssProp<'borderBottomWidth', 'size'>;
  borderBottomStyle?: ResponsiveCssProp<'borderBottomStyle'>;
  borderRadius?: ResponsiveCssProp<'borderRadius', 'size'>;
  borderTopLeftRadius?: ResponsiveCssProp<'borderTopLeftRadius', 'size'>;
  borderTopRightRadius?: ResponsiveCssProp<'borderTopRightRadius', 'size'>;
  borderBottomLeftRadius?: ResponsiveCssProp<'borderBottomLeftRadius', 'size'>;
  borderBottomRightRadius?: ResponsiveCssProp<
    'borderBottomRightRadius',
    'size'
  >;

  boxShadow?: ResponsiveCssProp<'boxShadow', 'shadow'>;

  container?: ResponsiveCssProp<'container'>;
  containerType?: ResponsiveCssProp<'containerType'>;
  containerName?: ResponsiveCssProp<'containerName'>;

  overflow?: ResponsiveCssProp<'overflow'>;
  overflowX?: ResponsiveCssProp<'overflowX'>;
  overflowY?: ResponsiveCssProp<'overflowY'>;
}
export const viewStylePropHandlers: StyleHandlers<SapphireViewStyleProps> = {
  ...commonStyleHandlers,
  ...flexStylePropsHandler,
  ...gridStylePropsHandler,
  display: 'display',
  color: 'color',
  backgroundColor: 'backgroundColor',
  border: 'border',
  borderWidth: 'borderWidth',
  borderColor: 'borderColor',
  borderStyle: 'borderStyle',
  borderLeft: 'borderLeft',
  borderLeftWidth: 'borderLeftWidth',
  borderLeftColor: 'borderLeftColor',
  borderLeftStyle: 'borderLeftStyle',
  borderRight: 'borderRight',
  borderRightWidth: 'borderRightWidth',
  borderRightColor: 'borderRightColor',
  borderRightStyle: 'borderRightStyle',
  borderTop: 'borderTop',
  borderTopWidth: 'borderTopWidth',
  borderTopColor: 'borderTopColor',
  borderTopStyle: 'borderTopStyle',
  borderBottom: 'borderBottom',
  borderBottomWidth: 'borderBottomWidth',
  borderBottomColor: 'borderBottomColor',
  borderBottomStyle: 'borderBottomStyle',
  borderRadius: 'borderRadius',
  borderTopLeftRadius: 'borderTopLeftRadius',
  borderTopRightRadius: 'borderTopRightRadius',
  borderBottomLeftRadius: 'borderBottomLeftRadius',
  borderBottomRightRadius: 'borderBottomRightRadius',
  boxShadow: 'boxShadow',
  container: 'container',
  containerType: 'containerType',
  containerName: 'containerName',
  overflow: 'overflow',
  overflowX: 'overflowX',
  overflowY: 'overflowY',
};

export const View = forwardRef(function View(
  { elementType: ElementType = 'div', children, ...otherProps }: ViewProps,
  ref: ForwardedRef<HTMLElement>
) {
  useThemeCheck();

  const { styleProps, filteredProps } = useSapphireStyleProps<
    ViewProps,
    SapphireViewStyleProps
  >(otherProps, viewStylePropHandlers);

  return (
    <ElementType {...filteredProps} {...styleProps} ref={ref}>
      {children}
    </ElementType>
  );
});
